import type { SessionRecapResponse } from "../../../shared/session-recap";
import { clockOrDateLabel } from "./soap-draft-provenance";

/** The owning RD edits and may retry; an Admin with rdId only reads. */
export type SessionRecapLane = "rd" | "read-only";

export type SessionRecapState =
  | { status: "absent" }
  | { status: "waiting" }
  | { status: "generating"; streamedText: string }
  | { status: "completed"; markdown: string; generatedAt: string }
  | { status: "failed"; retryAllowed: boolean };

export type SessionRecapAction =
  | { type: "read"; response: SessionRecapResponse | null }
  | { type: "delta"; text: string }
  | { type: "completed"; markdown: string; generatedAt: string }
  | { type: "failed"; retryAllowed: boolean }
  | { type: "retry_accepted" }
  | { type: "reset" };

export const INITIAL_SESSION_RECAP_STATE: SessionRecapState = { status: "absent" };

function stateFromRead(response: SessionRecapResponse | null): SessionRecapState {
  switch (response?.state) {
    case "waiting_for_transcript":
      return { status: "waiting" };
    case "generating":
      return { status: "generating", streamedText: "" };
    case "completed":
      return {
        status: "completed",
        markdown: response.recap.markdown,
        generatedAt: response.generatedAt,
      };
    case "failed":
      return { status: "failed", retryAllowed: response.failure.retryAllowed };
    default:
      return INITIAL_SESSION_RECAP_STATE;
  }
}

/** Local Recap state; a failure keeps only whether a retry is allowed, never partial text. */
export function sessionRecapReducer(
  state: SessionRecapState,
  action: SessionRecapAction
): SessionRecapState {
  switch (action.type) {
    case "read":
      return stateFromRead(action.response);
    case "delta":
      return state.status === "generating"
        ? { status: "generating", streamedText: state.streamedText + action.text }
        : state;
    case "completed":
      return {
        status: "completed",
        markdown: action.markdown,
        generatedAt: action.generatedAt,
      };
    case "failed":
      return { status: "failed", retryAllowed: action.retryAllowed };
    case "retry_accepted":
      return { status: "generating", streamedText: "" };
    case "reset":
      return INITIAL_SESSION_RECAP_STATE;
  }
}

type RecapKind =
  | "absent"
  | "waiting"
  | "generating"
  | "completed"
  | "failed"
  | "exhausted";

function recapKind(state: SessionRecapState): RecapKind {
  if (state.status === "failed") return state.retryAllowed ? "failed" : "exhausted";

  return state.status;
}

export type SessionRecapChipTone = "muted" | "ai" | "danger";
export type SessionRecapChipIcon =
  | "Clock"
  | "Loader2"
  | "Sparkles"
  | "CircleAlert"
  | "Info";

export type SessionRecapChipView =
  | { visible: false }
  | {
      visible: true;
      label: string;
      tone: SessionRecapChipTone;
      icon: SessionRecapChipIcon;
    };

const FAILED_LABEL = "Recap didn’t finish";

const CHIPS: Record<Exclude<RecapKind, "absent" | "failed">, SessionRecapChipView> = {
  waiting: {
    visible: true,
    label: "Recap after the transcript",
    tone: "muted",
    icon: "Clock",
  },
  generating: {
    visible: true,
    label: "Writing your recap…",
    tone: "ai",
    icon: "Loader2",
  },
  completed: { visible: true, label: "Session recap", tone: "ai", icon: "Sparkles" },
  exhausted: {
    visible: true,
    label: "No recap for this visit",
    tone: "muted",
    icon: "Info",
  },
};

export function chipView(
  state: SessionRecapState,
  lane: SessionRecapLane
): SessionRecapChipView {
  const kind = recapKind(state);
  if (kind === "absent") return { visible: false };

  if (kind !== "failed") return CHIPS[kind];

  return {
    visible: true,
    label: FAILED_LABEL,
    tone: lane === "rd" ? "danger" : "muted",
    icon: "CircleAlert",
  };
}

export type SessionRecapAlertCopy = { title: string; description: string };

export const FAILED_ALERT_RD_LANE: SessionRecapAlertCopy = {
  title: "Your recap didn’t finish.",
  description:
    "Nothing was kept. Your appointment note and SOAP draft aren’t affected.",
};

export const FAILED_ALERT_READ_ONLY: SessionRecapAlertCopy = {
  title: "The recap didn’t finish.",
  description: "Nothing was kept. The appointment note and SOAP draft aren’t affected.",
};

export const EXHAUSTED_ALERT: SessionRecapAlertCopy = {
  title: "We couldn’t write a recap for this visit.",
  description:
    "It didn’t finish twice, so it won’t retry. Your appointment note and SOAP draft aren’t affected.",
};

export type SessionRecapPanelBody =
  | { kind: "waiting" }
  | { kind: "generating"; streamedText: string }
  | { kind: "completed"; markdown: string }
  | { kind: "failed"; alert: "danger"; canRetry: boolean; copy: SessionRecapAlertCopy }
  | { kind: "exhausted"; alert: "default"; copy: SessionRecapAlertCopy };

export type SessionRecapPanelView = { subtitle: string; body: SessionRecapPanelBody };

export type SessionRecapClock = { now: string; timezone: string };

/**
 * "Recap generated 1:53 PM from today’s transcript." only for a generation on
 * the viewer's local today; any earlier day names the date and the visit.
 */
export function sessionRecapProvenanceLine(
  generatedAt: string,
  clock: SessionRecapClock
): string {
  const label = clockOrDateLabel(generatedAt, clock);
  const timeOnly = clockOrDateLabel(generatedAt, {
    now: generatedAt,
    timezone: clock.timezone,
  });
  const source = label === timeOnly ? "today’s transcript" : "the visit transcript";

  return `Recap generated ${label} from ${source}.`;
}

function failedPanel(lane: SessionRecapLane): SessionRecapPanelView {
  const rdLane = lane === "rd";

  return {
    subtitle: rdLane ? "Didn’t finish · you can try again" : "Didn’t finish",
    body: {
      kind: "failed",
      alert: "danger",
      canRetry: rdLane,
      copy: rdLane ? FAILED_ALERT_RD_LANE : FAILED_ALERT_READ_ONLY,
    },
  };
}

export function panelView(
  state: SessionRecapState,
  lane: SessionRecapLane,
  clock: SessionRecapClock
): SessionRecapPanelView | null {
  switch (state.status) {
    case "absent":
      return null;
    case "waiting":
      return {
        subtitle: "Arrives on its own after the transcript",
        body: { kind: "waiting" },
      };
    case "generating":
      return {
        subtitle: "Writing now · usually under a minute",
        body: { kind: "generating", streamedText: state.streamedText },
      };
    case "completed":
      return {
        subtitle: sessionRecapProvenanceLine(state.generatedAt, clock),
        body: { kind: "completed", markdown: state.markdown },
      };
    case "failed":
      return state.retryAllowed
        ? failedPanel(lane)
        : {
            subtitle: "Didn’t finish twice · won’t retry",
            body: { kind: "exhausted", alert: "default", copy: EXHAUSTED_ALERT },
          };
  }
}
